import React, { useState, useRef, useContext } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    TextField,
    IconButton,
    Button,
    CircularProgress,
    Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import axios from "axios";
import {
    DragDropContext,
    Droppable,
    Draggable,
} from "@hello-pangea/dnd";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useTranslation, ViewContext } from "components/lib";

export function AiContent({ apiKey, chapters, setChapters, basicInfo, setApiManageDialog }) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(0);
    const viewContext = useContext(ViewContext);
    const [generating, setGenerating] = useState({});
    const [isAnyGenerating, setIsAnyGenerating] = useState(false);
    const chapterRefs = useRef([]);
    const sectionRefs = useRef([]);


    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;
        const items = reorder(
            chapters,
            result.source.index,
            result.destination.index
        );
        setChapters(items);
    };

    const generateAIContent = async (chapterTitle, sectionTitle) => {
        try {
            const response = await axios.post(`/lab/report`, {
                apiKey: apiKey,
                projectTitle: basicInfo.title,
                projectType: basicInfo.projectType,
                technologies: basicInfo.technologies,
                chapterTitle: chapterTitle || "",
                sectionTitle: sectionTitle || "",
            });
            return response.data?.content || "";
        } catch (error) {
            viewContext.notification.show(t(`${error.response?.data?.message}`), 'error');
            throw error;
        }
    };

    const generateAllContent = async () => {
        setIsAnyGenerating(true);
        try {
            for (let cIdx = 0; cIdx < chapters.length; cIdx++) {
                setExpanded(cIdx);
                setGenerating((prev) => ({ ...prev, [`chapter-${cIdx}`]: true }));

                await new Promise((resolve) => setTimeout(resolve, 800));
                chapterRefs.current[cIdx]?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });

                const chapterContent = await generateAIContent(chapters[cIdx].title);
                setChapters((prev) =>
                    prev.map((ch, idx) =>
                        idx === cIdx ? { ...ch, content: chapterContent } : ch
                    )
                );
                setGenerating((prev) => ({ ...prev, [`chapter-${cIdx}`]: false }));

                for (let sIdx = 0; sIdx < chapters[cIdx].sections.length; sIdx++) {
                    setExpanded(cIdx);
                    setGenerating((prev) => ({
                        ...prev,
                        [`section-${cIdx}-${sIdx}`]: true,
                    }));

                    await new Promise((resolve) => setTimeout(resolve, 500));
                    sectionRefs.current[cIdx]?.[sIdx]?.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });

                    const sectionContent = await generateAIContent(
                        chapters[cIdx].title,
                        chapters[cIdx].sections[sIdx].title
                    );

                    setChapters((prev) =>
                        prev.map((ch, idx) =>
                            idx === cIdx
                                ? {
                                    ...ch,
                                    sections: ch.sections.map((sec, secIdx) =>
                                        secIdx === sIdx
                                            ? { ...sec, content: sectionContent }
                                            : sec
                                    ),
                                }
                                : ch
                        )
                    );

                    setGenerating((prev) => ({
                        ...prev,
                        [`section-${cIdx}-${sIdx}`]: false,
                    }));
                }
            }
        } catch (err) {
            viewContext.notification.show(t(`${err?.response?.data?.message}`), 'error')
        } finally {
            setIsAnyGenerating(false);
            setGenerating({});
        }
    };

    const handleChapterChange = (chapterIdx, field, value) => {
        const updated = chapters.map((ch, idx) =>
            idx === chapterIdx ? { ...ch, [field]: value } : ch
        );
        setChapters(updated);
    };

    const addChapter = () => {
        setChapters([
            ...chapters,
            {
                title: `Chapter ${chapters.length + 1}: New Chapter`,
                content: "",
                sections: [
                    {
                        title: "New Section",
                        content: ""
                    }
                ],
            },
        ]);
    };

    const handleSectionChange = (chapterIdx, sectionIdx, field, value) => {
        const updated = chapters.map((ch, idx) =>
            idx === chapterIdx
                ? {
                    ...ch,
                    sections: ch.sections.map((sec, sIdx) =>
                        sIdx === sectionIdx ? { ...sec, [field]: value } : sec
                    ),
                }
                : ch
        );
        setChapters(updated);
    };

    const addSection = (chapterIdx) => {
        const updated = chapters.map((ch, idx) =>
            idx === chapterIdx
                ? {
                    ...ch,
                    sections: [...ch.sections, { title: "New Section", content: "" }],
                }
                : ch
        );
        setChapters(updated);
    };

    const deleteSection = (chapterIdx, sectionIdx) => {
        const updated = chapters.map((ch, idx) =>
            idx === chapterIdx
                ? {
                    ...ch,
                    sections: ch.sections.filter((_, sIdx) => sIdx !== sectionIdx),
                }
                : ch
        );
        setChapters(updated);
    };

    const generateChapterContent = async (chapterIdx) => {
        const chapter = chapters[chapterIdx];
        setGenerating((prev) => ({ ...prev, [`chapter-${chapterIdx}`]: true }));
        setIsAnyGenerating(true);
        try {
            const content = await generateAIContent(chapter.title);
            handleChapterChange(chapterIdx, "content", content);
        } catch (error) {
            viewContext.notification.show(t(`${error?.response?.data?.message}`), 'error');
        } finally {
            setGenerating((prev) => ({ ...prev, [`chapter-${chapterIdx}`]: false }));
            setIsAnyGenerating(false);
        }
    };

    const generateSectionContent = async (chapterIdx, sectionIdx) => {
        const chapter = chapters[chapterIdx];
        const section = chapter.sections[sectionIdx];
        setGenerating((prev) => ({
            ...prev,
            [`section-${chapterIdx}-${sectionIdx}`]: true,
        }));
        setIsAnyGenerating(true);
        try {
            const content = await generateAIContent(chapter.title, section.title);
            handleSectionChange(chapterIdx, sectionIdx, "content", content);
        } catch (error) {
            viewContext.notification.show(t(`${error?.responce?.data?.message}`), 'error');
        } finally {
            setGenerating((prev) => ({
                ...prev,
                [`section-${chapterIdx}-${sectionIdx}`]: false,
            }));
            setIsAnyGenerating(false);
        }
    };

    return (
        <div style={{ width: "100%", paddingTop: "16px", paddingBottom: "16px" }}>
            <Box sx={{ display: "flex", width: '100%', mb: 2 }}>
                <Button
                    startIcon={
                        isAnyGenerating ? <CircularProgress size={16} /> : <AutoAwesomeIcon />
                    }
                    sx={{ width: '100%', margin: '0px 10px', marginTop: '8px', backgroundColor: "#1450a0", borderRadius: '8px', "&:hover": { backgroundColor: "#165bb6" } }}
                    variant="contained"
                    disabled={isAnyGenerating || !basicInfo.title}
                    onClick={() => {
                        if (!apiKey) {
                            setApiManageDialog(true);
                        } else {
                            generateAllContent();
                        }
                    }}
                >
                    {isAnyGenerating ? viewContext.t('Components.ProjectCommon.button.generatingall') : viewContext.t('Components.ProjectCommon.button.fullReport')}
                </Button>
            </Box>
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="chapters">
                    {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                            {chapters.map((chapter, index) => (
                                <Draggable
                                    key={index}
                                    draggableId={`chapter-${index}`}
                                    index={index}
                                >
                                    {(provided) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            style={{
                                                ...provided.draggableProps.style,
                                                marginBottom: "20px",
                                                backgroundColor: "#fefefe",
                                                padding: "0px 10px",
                                            }}
                                        >
                                            <Accordion
                                                disableGutters
                                                ref={(el) => (chapterRefs.current[index] = el)}
                                                expanded={expanded === index}
                                                onChange={(e, isExpanded) =>
                                                    setExpanded(isExpanded ? index : false)
                                                }
                                                sx={{
                                                    backgroundColor: "#fefefe",
                                                    '& .MuiCollapse-root': {
                                                        overflow: 'hidden',
                                                    },
                                                    "&:before": { display: "none" },
                                                    boxShadow: "2px 2px 6px rgba(0, 0, 0, 0.36)",
                                                }}
                                            >
                                                <AccordionSummary
                                                    expandIcon={<ExpandMoreIcon />}
                                                    {...provided.dragHandleProps}
                                                    sx={{
                                                        backgroundColor: "#fefefe",
                                                        borderRadius: "8px",
                                                        borderBottom: "1px solid #eee",
                                                    }}
                                                >
                                                    <span
                                                        {...provided.dragHandleProps}
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            cursor: "grab",
                                                            marginRight: 8,
                                                            color: "#888",
                                                        }}
                                                    >
                                                        <DragIndicatorIcon />
                                                    </span>
                                                    <TextField
                                                        multiline
                                                        value={chapter.title}
                                                        sx={{
                                                            "& fieldset": { border: 'none' },
                                                            width: "99%"
                                                        }}
                                                        onChange={(e) =>
                                                            setChapters((prev) =>
                                                                prev.map((ch, idx) =>
                                                                    idx === index
                                                                        ? { ...ch, title: e.target.value }
                                                                        : ch
                                                                )
                                                            )
                                                        }
                                                        variant="standard"
                                                        InputProps={{
                                                            style: { fontWeight: "bold", fontSize: "16px" },
                                                        }}
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setChapters((prev) =>
                                                                prev.filter((_, idx) => idx !== index)
                                                            );
                                                        }}
                                                        sx={{ ml: 1 }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </AccordionSummary>

                                                <AccordionDetails>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            mb: 2,
                                                            width: "100%",
                                                        }}
                                                    >
                                                        <Button
                                                            startIcon={
                                                                generating[`chapter-${index}`] ? (
                                                                    <CircularProgress size={16} />
                                                                ) : (
                                                                    <AutoAwesomeIcon />
                                                                )
                                                            }
                                                            size="small"
                                                            variant="outlined"
                                                            disabled={
                                                                isAnyGenerating || !basicInfo.title
                                                            }
                                                            onClick={() => {
                                                                if (!apiKey) {
                                                                    setApiManageDialog(true);
                                                                } else {
                                                                    generateChapterContent(index)
                                                                }
                                                            }}
                                                            sx={{
                                                                textTransform: "none",
                                                                fontSize: "12px",
                                                                borderRadius: "8px",
                                                                alignSelf: "flex-end",
                                                                mb: 1,
                                                                color: "#1450a0",
                                                                "&:hover": { color: "#165bb6" }
                                                            }}
                                                        >
                                                            {generating[`chapter-${index}`]
                                                                ? viewContext.t('Components.ProjectCommon.button.generating')
                                                                : viewContext.t('Components.ProjectCommon.button.generate')}
                                                        </Button>

                                                        <TextField
                                                            multiline
                                                            minRows={3}
                                                            placeholder={viewContext.t('Components.ProjectCommon.aicontent.placeholder.chapter', { chapterTitle: chapter.title })}
                                                            variant="outlined"
                                                            sx={{
                                                                width: "99%"
                                                            }}
                                                            value={chapter.content || ""}
                                                            onChange={(e) =>
                                                                handleChapterChange(index, "content", e.target.value)
                                                            }
                                                        />
                                                    </Box>

                                                    {chapter.sections.map((section, sIndex) => (
                                                        <Box key={sIndex}
                                                            ref={(el) => {
                                                                if (!sectionRefs.current[index]) sectionRefs.current[index] = [];
                                                                sectionRefs.current[index][sIndex] = el;
                                                            }}
                                                            sx={{ mb: 2, width: "100%" }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: "flex",
                                                                    justifyContent: "space-between",
                                                                    alignItems: "center",
                                                                    mb: 1,
                                                                }}
                                                            >
                                                                <TextField
                                                                    value={section.title}
                                                                    onChange={(e) =>
                                                                        handleSectionChange(
                                                                            index,
                                                                            sIndex,
                                                                            "title",
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                    variant="standard"
                                                                    sx={{
                                                                        "& fieldset": { border: 'none' },
                                                                        width: "99%"
                                                                    }}
                                                                    multiline
                                                                    InputProps={{
                                                                        style: { fontWeight: "bold" },
                                                                    }}
                                                                />

                                                                <Box sx={{ display: "flex", gap: 1 }}>
                                                                    <IconButton
                                                                        size="small"
                                                                        color="primary"
                                                                        disabled={
                                                                            isAnyGenerating || !basicInfo.title
                                                                        }
                                                                        sx={{
                                                                            color: "#1450a0",
                                                                            "&:hover": { color: "#165bb6" }
                                                                        }}
                                                                        onClick={() => {
                                                                            if (!apiKey) {
                                                                                setApiManageDialog(true);
                                                                            } else {
                                                                                generateSectionContent(index, sIndex);
                                                                            }
                                                                        }}
                                                                    >
                                                                        {generating[`section-${index}-${sIndex}`] ? (
                                                                            <CircularProgress size={16} />
                                                                        ) : (
                                                                            <AutoAwesomeIcon fontSize="small" />
                                                                        )}
                                                                    </IconButton>

                                                                    <IconButton
                                                                        size="small"
                                                                        color="error"
                                                                        onClick={() =>
                                                                            deleteSection(index, sIndex)
                                                                        }
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Box>
                                                            </Box>

                                                            <TextField
                                                                multiline
                                                                minRows={2}
                                                                sx={{
                                                                    width: "99%"
                                                                }}
                                                                placeholder={viewContext.t('Components.ProjectCommon.aicontent.placeholder.section', { sectionTitle: section.title })}
                                                                variant="outlined"
                                                                value={section.content || ""}
                                                                onChange={(e) =>
                                                                    handleSectionChange(
                                                                        index,
                                                                        sIndex,
                                                                        "content",
                                                                        e.target.value
                                                                    )
                                                                }
                                                            />
                                                        </Box>
                                                    ))}
                                                    <Button
                                                        startIcon={<AddIcon />}
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={() => addSection(index)}
                                                        sx={{ borderRadius: '8px', backgroundColor: "#1450a0", "&:hover": { backgroundColor: "#165bb6" } }}
                                                    >
                                                        {viewContext.t('Components.ProjectCommon.button.section')}
                                                    </Button>
                                                </AccordionDetails>
                                            </Accordion>
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

            <Button
                startIcon={<AddIcon />}
                variant="contained"
                color="primary"
                onClick={addChapter}
                sx={{ mt: 1, borderRadius: '8px', backgroundColor: "#1450a0", "&:hover": { backgroundColor: "#165bb6" } }}
            >
                {viewContext.t('Components.ProjectCommon.button.chapter')}
            </Button>
        </div>
    );
}